import React, { useState } from "react";
import SwipeableViews from "react-swipeable-views";
import MenuOption from "./menu-items/MenuOption";
import MenuCreateDialog from "./menu-items/MenuCreateDialog";
import CategorySelectDialog from "./menu-items/CategorySelectDialog";
import CreateCategory from "./menu-items/CreateCategory";
import AddMenuItemForm from "./menu-items/AddMenuItemForm";
import SelectItems from "./menu-items/SelectItems";
import MenuOcrFlow from "./menu-items/menu-ocr-ui/MenuOcrFlow";

/**
 * MenuSetup Component - Handles the complete menu/catalog setup flow
 * 
 * Navigation Flow:
 * - Step 0: Main listing (MenuOption component with tabs)
 * - Step 1: Menu creation/editing dialog
 * - Step 2: Category selection dialog (for menu)
 * - Step 3: Category creation/editing
 * - Step 4: Item selection dialog (for category)
 * - Step 5: Item creation/editing form
 * - Step 6: AI Menu OCR flow (scan/upload → process → confirm)
 * 
 * Context-Aware Navigation:
 * - Individual Mode (from tabs): After creating/editing items, go back to main listing
 * - Flow Mode (from menu/category creation): After creating/editing items, go back to item selection
 */
const MenuSetup = ({ catalogType, initialTab = 0 }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [editMenu, setEditMenu] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editCategory, setEditCategory] = useState(null);
  const [currentTab, setCurrentTab] = useState(initialTab);

  React.useEffect(() => {
    setCurrentTab(initialTab);
    setActiveStep(0);
    setEditItem(null);
    setEditMenu(null);
    setEditCategory(null);
    setSelectedCategories([]);
    setSelectedItems([]);
  }, [catalogType, initialTab]);

  const handleCreateOnMenuClick = () => {
    setActiveStep(6); // Move to AI OCR flow (step 6)
  };

  const handleEditMenu = (menu) => {
    setEditMenu(menu);
    setActiveStep(1); // Navigate to menu creation dialog with edit data
  };

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
    // Reset to first step when changing tabs
    setActiveStep(0);
    setEditMenu(null);
    setEditCategory(null);
    setSelectedCategories([]);
    setSelectedItems([]);
  };

  /**
   * Handle category addition based on current context:
   * - Menu tab (0): Go to category selection for menu creation
   * - Categories tab (1): Go directly to category creation
   */
  const handleAddCategory = () => {
    if (currentTab === 0) {
      // If we're in menu tab, go to category selection for menu
      setActiveStep(2);
    } else if (currentTab === 1) {
      // If we're in categories tab, go directly to category creation
      setActiveStep(3);
    }
  };

  /**
   * Handle category editing based on current context:
   * - Menu tab (0): Go to category selection (to assign to menu)
   * - Categories tab (1): Go directly to category editing
   */
  const handleEditCategory = (category) => {
    setEditCategory(category);
    if (currentTab === 0) {
      // If we're in menu tab, go to category selection
      setActiveStep(2);
    } else if (currentTab === 1) {
      // If we're in categories tab, go directly to category creation
      setActiveStep(3);
    }
  };

  return (
    <SwipeableViews
      enableMouseEvents={false}
      index={activeStep}
      onChangeIndex={setActiveStep}
    >
      <MenuOption
        isCatalog={catalogType === "catalog"}
        catalogType={catalogType}
        initialTab={currentTab}
        createOnMenuClick={handleCreateOnMenuClick}
        onEditMenu={handleEditMenu}
        onTabChange={handleTabChange}
        onAddCategory={handleAddCategory}
        onEditCategory={handleEditCategory}
        onEditItem={(item) => {
          setEditItem(item);
          setActiveStep(5);
        }}
        onAddItem={() => {
          setEditItem(null);
          setActiveStep(5);
        }}
      />
      <MenuCreateDialog
        isCatalog={catalogType === "catalog"}
        editMenu={editMenu}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        onClose={() => {
          setActiveStep(0);
          setSelectedCategories([]);
          setEditMenu(null); // Reset editMenu when closing
        }}
        onAddCategoryClick={() => {
          setActiveStep(2);
        }}
        onEditCategory={(category) => {
          setEditCategory(category);
          setActiveStep(3); // Navigate to category edit
        }}
      />
      <CategorySelectDialog
        isCatalog={catalogType === "catalog"}
        selectedCategories={selectedCategories}
        handleClose={() => {
          setActiveStep(1);
          setEditCategory(null); // Reset editCategory when going back
        }}
        onCreateCategory={() => {
          setActiveStep(3);
          setEditCategory(null); // Reset editCategory for new creation
        }}
        setSelectedCategories={setSelectedCategories}
        onEditCategory={(data) => {
          console.log("Setting editCategory:", data);
          setEditCategory(data);
          setActiveStep(3);
        }}
      />
      <CreateCategory
        isCatalog={catalogType === "catalog"}
        editCategory={editCategory}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onClose={() => {
          if (currentTab === 0) {
            setActiveStep(2); // Go back to category selection if in menu tab
          } else {
            setActiveStep(0); // Go back to main view if in categories tab
          }
          setEditCategory(null); // Reset editCategory when closing
        }}
        onMenuCreated={() => {
          if (currentTab === 0) {
            setActiveStep(1); // Go back to menu creation if in menu tab
          } else {
            setActiveStep(0); // Go back to main view if in categories tab
          }
          setEditCategory(null); // Reset editCategory after creation
        }}
        onAddItemClick={() => {
          setActiveStep(4);
        }}
      />
      <SelectItems
        isCatalog={catalogType === "catalog"}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onCreateItem={() => {
          setEditItem(null);
          setActiveStep(5);
        }}
        handleClose={() => {
          setActiveStep(1);
        }}
        onEditItem={(data) => {
          setEditItem(data);
          setActiveStep(5);
        }}
        onAdd={() => {
          setActiveStep(3);
        }}
      />
      {editItem ? (
        <AddMenuItemForm
          isCatalog={catalogType === "catalog"}
          editItem={editItem}
          handleClose={() => {
            setEditItem(null);
            // Navigation logic based on context:
            // - If we're in items tab (tab 2): we're in individual mode, go back to main listing
            // - If we're in menu/category tabs (tab 0/1): we're in flow mode, go back to SelectItems
            if (currentTab === 2) {
              setActiveStep(0); // Go back to main listing (items tab)
            } else {
              setActiveStep(4); // Go back to SelectItems (flow mode)
            }
          }}
        />
      ) : (
        <AddMenuItemForm
          isCatalog={catalogType === "catalog"}
          handleClose={() => {
            setEditItem(null);
            // Navigation logic based on context:
            // - If we're in items tab (tab 2): we're in individual mode, go back to main listing
            // - If we're in menu/category tabs (tab 0/1): we're in flow mode, go back to SelectItems
            if (currentTab === 2) {
              setActiveStep(0); // Go back to main listing (items tab)
            } else {
              setActiveStep(4); // Go back to SelectItems (flow mode)
            }
          }}
        />
      )}
      {/* Step 6: AI Menu OCR Flow */}
      <MenuOcrFlow
        isCatalog={catalogType === "catalog"}
        onClose={() => {
          setActiveStep(0); // Return to main listing
        }}
        onManualEntry={() => {
          setEditMenu(null);
          setActiveStep(1); // Go to existing manual MenuCreateDialog
        }}
      />
    </SwipeableViews>
  );
};

export default MenuSetup;
